import { useEffect, useRef, useState } from "react";

import { useUser } from "../../user/utils/user";

import { unwrapError } from "../../common/utils/api";
import { downloadFile } from "../../common/utils/helpers";
import { clearDB, exportDB, importDB } from "../../data/utils/data";
import { syncImages as syncImagesUtil } from "../../data/utils/syncImages";
import SyncProgressIndicator from "../components/SyncProgressIndicator";
import { StorageUtils } from "../components/storageWarning";
import useUploadProgress from "../hooks/useUploadProgress";

export default function Debug({ showAlert }) {
  const { user } = useUser();
  const [data, setData] = useState("");
  const [availableSpace, setAvailableSpace] = useState("");
  const [totalSpace, setTotalSpace] = useState("");
  const importInputRef = useRef();
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncErrors, setSyncErrors] = useState([]);
  const {
    uploadProgress,
    onProgress,
    addTotalToProgress,
    resetUploadProgress,
  } = useUploadProgress();

  function updateData() {
    exportDB(function (err, jsonString) {
      if (err) {
        console.error(err);
      } else {
        setData(jsonString);
      }
    });
  }

  function clearData() {
    clearDB(function (err) {
      if (err) {
        console.error(err);
      } else {
        updateData();
      }
    });
  }

  function importData(data) {
    importDB(data, function (err) {
      if (err) {
        console.error(err);
      } else {
        updateData();
      }
    });
  }

  function exportData() {
    downloadFile({
      data: data,
      fileName: "export.json",
      fileType: "application/json",
    });
  }

  function handleImportClick() {
    importInputRef.current.click();
  }
  function handleImportChange(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = function (readerEvent) {
      importData(readerEvent.target.result);
    };
    reader.readAsText(file);
  }

  async function syncImages() {
    setIsSyncing(true);
    resetUploadProgress();
    setSyncErrors([]);
    const collectedErrors = [];
    try {
      await syncImagesUtil(onProgress, addTotalToProgress, {
        continueOnError: true,
        onError: ({ recordId, recordName, message }) => {
          collectedErrors.push({ recordId, recordName, message });
        },
      });
      setSyncErrors(collectedErrors);
      if (typeof showAlert === "function") {
        if (collectedErrors.length > 0) {
          const n = collectedErrors.length;
          showAlert({
            type: "error",
            message: `Image sync failed with ${n} record error${
              n > 1 ? "s" : ""
            }. See details below.`,
          });
        } else {
          showAlert({ type: "success", message: "Image sync completed" });
        }
      }
    } catch (error) {
      const message =
        (error && error.response && error.response.data) || unwrapError(error);
      if (typeof showAlert === "function") {
        showAlert({ type: "error", message });
      }
    } finally {
      setIsSyncing(false);
    }
  }

  useEffect(updateData, []);

  // add available space and total space from StorageUtils
  useEffect(() => {
    function fetchStorageInfo() {
      if (navigator.storage?.estimate) {
        navigator.storage.estimate().then(({ usage, quota }) => {
          const availableBytes = quota - usage;

          setAvailableSpace(StorageUtils.formatBytes(availableBytes));
          setTotalSpace(StorageUtils.formatBytes(quota));
        });
      }
    }

    fetchStorageInfo();
  }, []);

  return (
    <div className="text-center space-y-3">
      <SyncProgressIndicator open={isSyncing} uploadProgress={uploadProgress} />
      <h3 className="mt-2 text-sm font-medium text-gray-900">
        Current user information:
      </h3>
      <code
        className="mt-2 block whitespace-pre-wrap overflow-x-scroll"
        data-testid="user-info"
      >
        {JSON.stringify(user)}
      </code>

      <h3
        className="mt-2 text-sm font-medium text-gray-900"
        data-testid="database-size"
      >
        Current database size: {data.length}
      </h3>
      <h3
        className="mt-2 text-sm font-medium text-gray-900"
        data-testid="available-space"
      >
        Available space: {availableSpace}
      </h3>
      <h3
        className="mt-2 text-sm font-medium text-gray-900"
        data-testid="total-space"
      >
        Total space: {totalSpace}
      </h3>
      <div>
        <button
          className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          onClick={exportData}
        >
          Export current database
        </button>
      </div>

      <div>
        <button
          className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          onClick={clearData}
        >
          Clear database
        </button>
      </div>

      <div>
        <button
          className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          onClick={handleImportClick}
        >
          Import database
        </button>
      </div>
      <div>
        <button
          className="ml-3 inline-flex justify-center rounded-md border border-transparent bg-cyan-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:bg-gray-500 disabled:cursor-not-allowed"
          onClick={syncImages}
        >
          Sync images
        </button>
      </div>

      <input
        data-testid="import-database"
        type="file"
        ref={importInputRef}
        onChange={handleImportChange}
        style={{ display: "none" }}
      />

      {syncErrors.length > 0 && (
        <div className="mt-4 text-left">
          <h3 className="text-sm font-medium text-red-700">
            Image sync failures
          </h3>
          <div className="mt-2 border border-red-200 rounded-md p-3 bg-red-50">
            <ul className="list-disc pl-5 space-y-1">
              {syncErrors.map((e, idx) => (
                <li key={idx} className="text-sm text-red-800">
                  {(e.recordName || "Record") +
                    (e.recordId ? ` [${e.recordId}]` : "")}
                  : {e.message}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <h3 className="mt-6 text-sm font-medium text-gray-900">
        Build information
      </h3>
      <div className="text-xs font-medium text-gray-900">
        <p>Build date: {process.env.REACT_APP_CI_COMMIT_TIMESTAMP}</p>
        <p>Branch: {process.env.REACT_APP_CI_COMMIT_REF_NAME}</p>
        <p>Commit: {process.env.REACT_APP_CI_COMMIT_SHA}</p>
        <p>Pipeline: {process.env.REACT_APP_CI_PIPELINE_ID}</p>
        <p>Project Pipeline: {process.env.REACT_APP_CI_PIPELINE_IID}</p>
      </div>
    </div>
  );
}
